import { useState } from 'react';
import {
  DiagramIcon,
  DIAGRAM_ICON_KEYS,
  DIAGRAM_ICON_MAP,
  DIAGRAM_ICON_COLORS,
  DEFAULT_DIAGRAM_ICON,
  DEFAULT_DIAGRAM_ICON_COLOR,
} from './diagramIcons';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';

interface DiagramIconPickerProps {
  icon?: string | null;
  color?: string | null;
  onChange: (icon: string, color: string) => void;
  /** Sizing classes for the trigger tile (default h-7 w-7). */
  triggerClassName?: string;
  /** Which edge the panel aligns to. */
  align?: 'left' | 'right';
}

/**
 * Notion/Linear-style icon picker: a trigger tile showing the current icon;
 * clicking opens a color row + curated Solar icon grid. Built on the shadcn
 * Popover so it portals out of overflow-hidden containers (e.g. modals),
 * flips up when there's no room below, and composes inside a Dialog.
 */
export function DiagramIconPicker({
  icon,
  color,
  onChange,
  triggerClassName,
  align = 'left',
}: DiagramIconPickerProps) {
  const [open, setOpen] = useState(false);
  const curColor = color || DEFAULT_DIAGRAM_ICON_COLOR;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Change icon"
          className={`ss-press grid place-items-center rounded-md hover:bg-muted ${triggerClassName ?? 'h-7 w-7'}`}
        >
          <DiagramIcon name={icon} color={curColor} className="h-[18px] w-[18px]" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align={align === 'right' ? 'end' : 'start'}
        sideOffset={6}
        collisionPadding={8}
        className="w-[268px] rounded-xl p-2.5 shadow-xl"
      >
        <div className="mb-2 flex flex-wrap gap-1.5 border-b border-border pb-2.5">
          {DIAGRAM_ICON_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => onChange(icon || DEFAULT_DIAGRAM_ICON, c)}
              aria-label={`Set color ${c}`}
              className={`ss-press h-[19px] w-[19px] rounded-full ${
                curColor.toLowerCase() === c.toLowerCase()
                  ? 'ring-2 ring-offset-2 ring-offset-[var(--popover)] ring-[var(--foreground)]'
                  : ''
              }`}
              style={{ background: c }}
            />
          ))}
        </div>

        <div className="grid max-h-[212px] grid-cols-8 gap-0.5 overflow-y-auto">
          {DIAGRAM_ICON_KEYS.map((k) => {
            const Comp = DIAGRAM_ICON_MAP[k];
            const selected = icon === k;
            return (
              <button
                key={k}
                type="button"
                onClick={() => {
                  onChange(k, curColor);
                  setOpen(false);
                }}
                aria-label={k}
                className={`ss-press grid h-[28px] w-[28px] place-items-center rounded-md hover:bg-muted ${
                  selected ? 'bg-muted' : ''
                }`}
              >
                <Comp
                  className="h-4 w-4"
                  style={{ color: selected ? curColor : 'var(--muted-foreground)' }}
                />
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
